export const LAZY_CHUNKS_ELEMENT_ID = 'lazy-chunks';

export type LazyChunks = { mainPages: string[]; rest: string[] };

const readLazyChunks = (): LazyChunks => {
  const json = document.getElementById(LAZY_CHUNKS_ELEMENT_ID)?.textContent;
  return json ? JSON.parse(json) : { mainPages: [], rest: [] };
};

const prefetch = (href: string) =>
  new Promise<void>(resolve => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    // Matches the request mode of a module script, so the import reuses the cached response.
    link.crossOrigin = 'anonymous';
    link.onload = () => resolve();
    link.onerror = () => resolve();
    document.head.append(link);
  });

const prefetchMainPagesFirst = async () => {
  const { mainPages, rest } = readLazyChunks();
  await Promise.all(mainPages.map(prefetch));
  await Promise.all(rest.map(prefetch));
};

const whenIdle = (callback: () => void) => {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(callback);
  } else {
    setTimeout(callback);
  }
};

const afterFirstContentfulPaint = (callback: () => void) => {
  if (!PerformanceObserver.supportedEntryTypes?.includes('paint')) {
    callback();
    return;
  }
  const observer = new PerformanceObserver(list => {
    if (list.getEntriesByName('first-contentful-paint').length) {
      observer.disconnect();
      callback();
    }
  });
  observer.observe({ type: 'paint', buffered: true });
};

/**
 * Caches the code of each route, so a first visit to a route does not wait on the network. Call it after the
 * first route loads. Prefetches that start before first paint compete with the code and data the page needs.
 * The main pages go first, and the rest wait for them, so the pages that most visits go to next load soonest.
 */
export const prefetchLazyChunksAfterFirstPaint = () => {
  afterFirstContentfulPaint(() => whenIdle(() => void prefetchMainPagesFirst()));
};
